/**
 * One column of the Change Plan table. Business logic (which action label
 * applies, whether the click needs the downgrade-blocker check first) stays
 * in ChangePlanPage.js — this component only renders a plan/price pair.
 */
import React, { useCallback } from "react";
import classNames from "classnames";
import Button from "../../theme/untitled/Button/Button";
import Icon from "../../theme/untitled/icons/Icon";
import styles from "./PlanColumn.module.scss";

const applyDiscount = (amount, discount) => {
  if (!discount) {
    return amount;
  }
  if (discount.amount_off) {
    return amount - discount.amount_off / 100;
  }
  if (discount.percent_off) {
    return amount - (amount * discount.percent_off) / 100;
  }
  return amount;
};

const money = (cents, currency, discount) => {
  const amount = applyDiscount((cents || 0) / 100, discount);
  return new Intl.NumberFormat(window.navigator.language || "en-US", {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 0,
  }).format(amount);
};

/**
 * @param {Object} plan - Stripe product (name, description, marketing_features)
 * @param {Object} monthlyPrice - Stripe price, interval=month
 * @param {Object} yearlyPrice - Stripe price, interval=year
 * @param {'month'|'year'} interval - currently toggled interval
 * @param {string} currency
 * @param {Object=} discount
 * @param {boolean} isCurrent
 * @param {boolean} isBest
 * @param {string} actionLabel
 * @param {boolean} disabled
 * @param {function} onAction
 */
const PlanColumn = ({
  plan,
  monthlyPrice,
  yearlyPrice,
  interval,
  currency,
  discount,
  isCurrent,
  isBest,
  actionLabel,
  disabled,
  onAction,
}) => {
  const isFree = !monthlyPrice?.unit_amount && !yearlyPrice?.unit_amount;
  const isYearly = interval === "year";

  let priceBlock;
  if (isFree) {
    priceBlock = (
      <div className={styles.price}>
        <div className={styles["now-price"]}>
          <span className={styles.amount}>{money(0, currency, null)}</span>
          <span className={styles.interval}>/month</span>
        </div>
        <p className={styles.billNote}>Free forever, no card needed</p>
      </div>
    );
  } else if (isYearly && yearlyPrice) {
    const equivalent = Math.round(yearlyPrice.unit_amount / 12);
    priceBlock = (
      <div className={styles.price}>
        {monthlyPrice && (
          <div className={styles.was}>
            {money(monthlyPrice.unit_amount, currency, null)}/month
          </div>
        )}
        <div className={styles["now-price"]}>
          <span className={styles.amount}>
            {money(equivalent, currency, discount)}
          </span>
          <span className={styles.interval}>/month</span>
        </div>
        <p className={styles.billNote}>
          {money(yearlyPrice.unit_amount, currency, discount)} billed yearly
        </p>
      </div>
    );
  } else {
    priceBlock = (
      <div className={styles.price}>
        <div className={styles["now-price"]}>
          <span className={styles.amount}>
            {monthlyPrice
              ? money(monthlyPrice.unit_amount, currency, discount)
              : "—"}
          </span>
          <span className={styles.interval}>/month</span>
        </div>
        {yearlyPrice && (
          <p className={styles.billNote}>
            {money(yearlyPrice.unit_amount, currency, discount)} if billed
            yearly
          </p>
        )}
      </div>
    );
  }

  const activePrice = isYearly
    ? yearlyPrice || monthlyPrice
    : monthlyPrice || yearlyPrice;

  const handleAction = useCallback(() => {
    if (activePrice) {
      onAction(activePrice, plan);
    }
  }, [activePrice, onAction, plan]);

  let flagBadge = null;
  if (isCurrent) {
    flagBadge = <span className={styles.flag}>Current plan</span>;
  } else if (isBest) {
    flagBadge = (
      <span className={classNames(styles.flag, styles["flag--neutral"])}>
        Best deal
      </span>
    );
  }

  const featureItems = plan.marketing_features?.map((feature, idx) => (
    <li key={idx}>
      <Icon name="Check" size={16} className={styles.checkIcon} />
      {feature.name}
    </li>
  ));

  let buttonHierarchy = "secondaryGray";
  if (!isCurrent && isBest) {
    buttonHierarchy = "primary";
  }

  return (
    <div
      className={classNames(styles.col, {
        [styles.best]: isBest && !isCurrent,
        [styles.now]: isCurrent,
      })}
    >
      {flagBadge}
      <h3 className={styles.name}>{plan.name}</h3>
      <p className={styles.tag}>{plan.description}</p>
      {priceBlock}
      <div className={styles.cta}>
        <Button
          hierarchy={buttonHierarchy}
          disabled={disabled || isCurrent}
          onClick={handleAction}
        >
          {actionLabel}
        </Button>
      </div>
      <ul className={styles.includes}>
        <li className={styles.includesHeading}>This includes</li>
        {featureItems}
      </ul>
      {activePrice && !activePrice.livemode && (
        <div className={styles.sandbox}>Sandbox price</div>
      )}
    </div>
  );
};

export default PlanColumn;
